import { DynamicComponentExitReason } from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import { getKeys, noOp } from '@treasury/utils';
import {
    DialogButton,
    DialogButtonPosture,
    OmegaDialogButtonMap,
    OmegaDialogButtonsOptional,
    OmegaDialogOptions,
    OmegaDialogOptionsAll,
} from './omega-dialog.service.types';

const defaultOptions: OmegaDialogOptionsAll = Object.freeze({
    buttons: {
        [DynamicComponentExitReason.Confirm]: {
            label: 'OK',
            onClick: noOp,
            posture: DialogButtonPosture.Primary,
        },
        [DynamicComponentExitReason.Cancel]: {
            label: 'Cancel',
            onClick: noOp,
            posture: DialogButtonPosture.Secondary,
        },
        [DynamicComponentExitReason.ForceClose]: null,
    },
    host: window.document.body,
    renderButtons: true,
    preventClose: false,
    flexDirection: 'row',
    height: 100,
    heightUnits: '%',
    width: 100,
    widthUnits: '%',
});

export function normalizeOptions(options: OmegaDialogOptions): OmegaDialogOptionsAll {
    const buttons = normalizeButtons(options.buttons);

    if (options.height && !options.heightUnits) {
        options.heightUnits = 'px';
    }

    if (options.width && !options.widthUnits) {
        options.widthUnits = 'px';
    }

    return {
        ...defaultOptions,
        ...options,
        ...{
            // merge nested properties this way until a deep merge helper exists
            buttons,
        },
    };
}

/**
 * Use buttons verbatim if provided.
 * Otherwise, use the default buttons for the common case.
 * Do not mix them together so that consumers can intentionally omit buttons.
 *
 * If a button is provided, it will have its details filled in if absent.
 * This makes it easy to declare buttons without, e.g., needing to specify the default label.
 */
function normalizeButtons(buttons?: OmegaDialogButtonsOptional): OmegaDialogButtonMap {
    if (!buttons) {
        return defaultOptions.buttons;
    }

    return getKeys(buttons).reduce((normalizedButtons, key) => {
        const defaultButton = defaultOptions.buttons[key];
        const button = buttons?.[key];

        // fill in the default with the default button
        const mergedButton = { ...defaultButton, ...button } as DialogButton;

        normalizedButtons[key] =
            // support explicitly removing buttons with null value
            button === null ? null : mergedButton;
        return normalizedButtons;
    }, {} as OmegaDialogButtonMap);
}
